// SB6-ANA-FILT-06 (Track D / D9) — orchestration hook shared by both
// advanced widgets to drive the save/load presets control + the implicit
// last-used persistence.
//
// It glues the `useFilterConfigs` CRUD hooks to a widget's live config
// blob and exposes everything the `FilterConfigPresets` control needs,
// plus the two D9 behaviours that aren't UI:
//
//   * **Last-used upsert on change** — on every COMMITTED config change the
//     last-used row (`name: null`) is upserted (POST). The write is
//     DEBOUNCED (default 500ms) so slider drags don't spam the endpoint
//     (D9). It only fires after the auto-restore has run, so opening a DA
//     never immediately re-writes the row it just read.
//
//   * **Validity-gated restore on open** — once the list query settles, the
//     last-used row is applied to the widget (via `applyBlob`), dropping any
//     checked option that no longer has data in the current facets and any
//     blob whose signature differs (the list is already signature-filtered,
//     so a mismatched row never arrives). Runs exactly once per
//     `(kind, modelSignature)`; never resets on its own otherwise.
//
// The widget supplies the current `blob` (serialised from its live state)
// and an `applyBlob` callback (writes a blob back into its state). The hook
// stays agnostic about the blob's internal shape — that lives in
// `filterConfigBlob.ts`.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FilterConfig } from "@aeriosiq/types";
import {
  partitionConfigs,
  useCreateFilterConfigMutation,
  useDeleteFilterConfigMutation,
  useFilterConfigsQuery,
  useUpdateFilterConfigMutation,
  type FilterConfigScope,
} from "./useFilterConfigs.js";

/** Debounce window for the last-used upsert (D9). */
export const LAST_USED_DEBOUNCE_MS = 500;

export interface UseWidgetFilterConfigsArgs {
  /** The `(kind, modelSignature)` scope. Empty signature ⇒ idle. */
  scope: FilterConfigScope;
  /**
   * The widget's current config blob, serialised from its live state. The
   * hook compares its JSON to the last-applied value to decide whether to
   * upsert the last-used row. Pass a STABLE object (memoise upstream) or a
   * value whose JSON is stable across renders.
   */
  blob: Record<string, unknown>;
  /**
   * Whether the facets the widget needs have settled. The auto-restore
   * waits for this so validity-gating sees the real present-options set.
   */
  facetsReady: boolean;
  /**
   * Apply a stored blob to the widget state. The hook calls this exactly
   * once on auto-restore and again whenever the user loads a named preset.
   * The widget is responsible for validity-gating inside this callback.
   */
  applyBlob: (blob: Record<string, unknown>) => void;
}

export interface UseWidgetFilterConfigsResult {
  /** Named presets for the dropdown (last-used row excluded). */
  presets: FilterConfig[];
  /** The currently-loaded preset id (`""` when none / ad-hoc edits). */
  selectedId: string;
  /** True until the signature settles; disables the presets control. */
  disabled: boolean;
  /** A save/update/delete is in flight. */
  busy: boolean;
  /** Dropdown selection handler (loads the picked preset's blob). */
  onSelect: (configId: string) => void;
  /** "Save as…" — create a named preset from the current blob. */
  onSaveAs: (name: string) => void;
  /** "Update" — overwrite a preset's blob with the current one. */
  onUpdate: (configId: string) => void;
  /** "Delete" — remove a preset. */
  onDelete: (configId: string) => void;
}

export function useWidgetFilterConfigs({
  scope,
  blob,
  facetsReady,
  applyBlob,
}: UseWidgetFilterConfigsArgs): UseWidgetFilterConfigsResult {
  const hasSignature = scope.modelSignature.length > 0;

  const { data: configs, isSuccess } = useFilterConfigsQuery(scope);
  const create = useCreateFilterConfigMutation(scope);
  const update = useUpdateFilterConfigMutation(scope);
  const remove = useDeleteFilterConfigMutation(scope);

  const { presets, lastUsed } = useMemo(
    () => partitionConfigs(configs ?? []),
    [configs],
  );

  // The currently-loaded preset id. Selecting `""` clears it. State (not a
  // ref) so save/delete that change it re-render the dropdown.
  const [selectedIdState, setSelectedIdState] = useState<string>("");
  const blobJson = useMemo(() => JSON.stringify(blob), [blob]);

  // --- Auto-restore (validity-gated, once per signature) ----------------
  // Keyed by `(kind, modelSignature)` so switching DAs (new signature) runs
  // it again, but re-renders within the same DA do not.
  const restoredKeyRef = useRef<string | null>(null);
  const restoreKey = `${scope.kind}::${scope.modelSignature}`;
  // After auto-restore we capture the applied JSON so the last-used upsert
  // does NOT fire for the value we just read back (D9 — never re-write on
  // open). Null until the first restore has been considered.
  const lastAppliedJsonRef = useRef<string | null>(null);

  useEffect(() => {
    if (!hasSignature || !isSuccess || !facetsReady) return;
    if (restoredKeyRef.current === restoreKey) return;
    restoredKeyRef.current = restoreKey;
    if (lastUsed) {
      applyBlob(lastUsed.config);
    }
    // Seed the baseline so the first change AFTER restore is what triggers
    // the upsert (not the restore itself). When there is no last-used row,
    // seed with the current blob so an unchanged widget never writes.
    lastAppliedJsonRef.current = lastUsed
      ? JSON.stringify(lastUsed.config)
      : blobJson;
  }, [
    hasSignature,
    isSuccess,
    facetsReady,
    restoreKey,
    lastUsed,
    applyBlob,
    blobJson,
  ]);

  // --- Debounced last-used upsert on change (D9) ------------------------
  // Mutate via a ref so the debounce effect needn't list the (per-render)
  // mutation object in its deps — re-arming on every mutation state change
  // would defeat the debounce.
  const createMutate = create.mutate;
  const createMutateRef = useRef(createMutate);
  createMutateRef.current = createMutate;
  const upsertTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    // Only after the restore baseline is established, and only when the
    // blob actually changed from what we last persisted/applied.
    if (!hasSignature) return;
    if (lastAppliedJsonRef.current === null) return;
    if (lastAppliedJsonRef.current === blobJson) return;
    if (upsertTimer.current) clearTimeout(upsertTimer.current);
    const json = blobJson;
    upsertTimer.current = setTimeout(() => {
      lastAppliedJsonRef.current = json;
      createMutateRef.current({
        kind: scope.kind,
        name: null,
        modelSignature: scope.modelSignature,
        config: JSON.parse(json) as Record<string, unknown>,
      });
    }, LAST_USED_DEBOUNCE_MS);
    return () => {
      if (upsertTimer.current) clearTimeout(upsertTimer.current);
    };
  }, [blobJson, hasSignature, scope.kind, scope.modelSignature]);

  // --- Presets actions --------------------------------------------------
  const onSelect = useCallback(
    (configId: string) => {
      setSelectedIdState(configId);
      const preset = presets.find((p) => p.id === configId);
      if (preset) {
        // Loading a preset applies its blob; treat the applied value as the
        // new baseline so it isn't immediately re-upserted as last-used.
        applyBlob(preset.config);
        lastAppliedJsonRef.current = JSON.stringify(preset.config);
      }
    },
    [presets, applyBlob],
  );

  const onSaveAs = useCallback(
    (name: string) => {
      create.mutate(
        {
          kind: scope.kind,
          name,
          modelSignature: scope.modelSignature,
          config: JSON.parse(blobJson) as Record<string, unknown>,
        },
        {
          onSuccess: (saved) => {
            setSelectedIdState(saved.id);
            // The just-saved blob IS the live state; mark it the baseline so
            // it isn't redundantly re-upserted as the last-used row.
            lastAppliedJsonRef.current = blobJson;
          },
        },
      );
    },
    [create, scope.kind, scope.modelSignature, blobJson],
  );

  const onUpdate = useCallback(
    (configId: string) => {
      update.mutate({
        configId,
        body: { config: JSON.parse(blobJson) as Record<string, unknown> },
      });
    },
    [update, blobJson],
  );

  const onDelete = useCallback(
    (configId: string) => {
      remove.mutate(configId, {
        onSettled: () => {
          setSelectedIdState((cur) => (cur === configId ? "" : cur));
        },
      });
    },
    [remove],
  );

  // Clear a stale selection when the loaded preset disappears from the list
  // (e.g. deleted in another tab) so the dropdown doesn't show a ghost id.
  const selectedId = presets.some((p) => p.id === selectedIdState)
    ? selectedIdState
    : "";

  return {
    presets,
    selectedId,
    disabled: !hasSignature,
    busy: create.isPending || update.isPending || remove.isPending,
    onSelect,
    onSaveAs,
    onUpdate,
    onDelete,
  };
}
